// Category page: breadcrumb (Store > Category), title, grid of families

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCategories, useProductFamilies } from '@/hooks/use-store';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import SEO from '@/components/SEO';
import { toAbsoluteImageUrl } from '@/lib/store-images';
import { SITE_URL, sitePath } from '@/lib/site-config';
import { Card, CardHeader } from '@/components/ui/card';
import { StoreCardDescription } from '@/components/store/StoreCardDescription';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { postWorkerApi } from '@/lib/worker-api';
import { Sparkles, Loader2 } from 'lucide-react';
import type { ProductFamily } from '@/lib/store-types';

const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL ?? '';

const StoreCategory = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const category = categories?.find((c) => c.slug === categorySlug);
  const categoryId = category?.id;
  // Only fetch families when we have a categoryId; avoids fetching "all families" while categories are loading
  const { data: families, isLoading: familiesLoading } = useProductFamilies(
    categoryId ?? null,
    { enabled: !!categoryId }
  );
  const isLoading = categoriesLoading || (!!categoryId && familiesLoading);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isAdmin, setIsAdmin] = useState(false);
  const [generatingOneIds, setGeneratingOneIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const session = data?.session;
      setIsAdmin((session?.user?.email?.toLowerCase() ?? '') === ADMIN_EMAIL.toLowerCase());
    });
  }, []);

  async function handleGenerateOneFamily(family: ProductFamily, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setGeneratingOneIds((prev) => new Set(prev).add(family.id));
    try {
      const result = await postWorkerApi<{ imageUrl?: string }>('/api/generate-catalog-image', {
        type: 'family',
        id: family.id,
        confirm: true,
        background: true,
      });
      if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);
      toast({
        title: result.status === 202 ? 'Generando en segundo plano' : 'Imagen generada',
        description: result.status === 202
          ? `Imagen para «${family.family_name}» se actualizará en unos segundos.`
          : 'Portada actualizada.',
      });
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      if (result.status === 202) {
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] }), 15000);
      }
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Error al generar', variant: 'destructive' });
    } finally {
      setGeneratingOneIds((prev) => {
        const next = new Set(prev);
        next.delete(family.id);
        return next;
      });
    }
  }

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: category?.name ?? categorySlug ?? 'Category' },
  ];

  const seoBreadcrumbs: Array<{ name: string; url: string }> = [
    { name: 'Store', url: sitePath('/store') },
    ...(category && categorySlug
      ? [{ name: category.name, url: sitePath(`/store/category/${categorySlug}`) }]
      : []),
  ];

  const categoryDescription =
    category && (category.description?.trim() || `Productos de ${category.name} en Padmora B2B`);
  const categoryStructuredData =
    category && categorySlug
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            { '@type': 'Organization', name: 'Padmora', url: SITE_URL, logo: sitePath('/android-chrome-512x512.png') },
            {
              '@type': 'CollectionPage',
              name: category.name,
              description: categoryDescription,
              url: sitePath(`/store/category/${categorySlug}`),
            },
          ],
        }
      : undefined;

  return (
    <div className="space-y-6">
      {category && categorySlug && (
        <SEO
          title={`${category.name} | Tienda B2B Padmora`}
          description={
            category.description?.trim() ||
            `Productos de ${category.name} en Padmora B2B`
          }
          image={toAbsoluteImageUrl(category.cover_image ?? category.thumbnail) ?? undefined}
          canonical={sitePath(`/store/category/${categorySlug}`)}
          breadcrumbs={seoBreadcrumbs}
          structuredData={categoryStructuredData}
        />
      )}
      <StoreBreadcrumbs segments={breadcrumbs} />
      <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">{category?.name ?? categorySlug}</h1>
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : !category ? (
        <p className="text-zinc-600">No se encontró la categoría.</p>
      ) : (families ?? []).length === 0 ? (
        <p className="text-zinc-600">No hay familias en esta categoría.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {families!.map((fam) => (
            <Card key={fam.id} className="overflow-hidden transition-shadow hover:shadow-md border-zinc-200 flex flex-col">
              <Link to={`/store/category/${categorySlug}/${fam.slug}`} className="group block flex-1 min-w-0">
                {fam.showroom_image && (
                  /* Imagen cuadrada 1:1 (ratio mantenido); más columnas = cards más pequeñas. */
                  <div
                    className="relative w-full rounded-t-lg bg-zinc-100 overflow-hidden"
                    style={{ paddingBottom: '100%' }}
                  >
                    <img
                      src={fam.showroom_image}
                      alt=""
                      className="absolute top-0 left-0 w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-200"
                      loading="lazy"
                    />
                  </div>
                )}
                <CardHeader className="font-semibold text-zinc-900 py-2 px-3 text-sm min-w-0">{fam.family_name}</CardHeader>
                {fam.marketing_description && (
                  <StoreCardDescription
                    wrapperClassName="px-3 pb-5"
                    className="text-xs text-zinc-600"
                  >
                    {fam.marketing_description}
                  </StoreCardDescription>
                )}
              </Link>
              {isAdmin && (
                <div className="p-2 border-t border-zinc-100 bg-zinc-50/60">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleGenerateOneFamily(fam, e)}
                    disabled={generatingOneIds.has(fam.id)}
                    className="gap-1 w-full sm:w-auto min-w-[110px]"
                    title={fam.showroom_image ? 'Regenerar imagen con IA' : 'Generar imagen con IA'}
                  >
                    {generatingOneIds.has(fam.id) ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    {generatingOneIds.has(fam.id) ? 'Generando imagen…' : 'Generar imagen'}
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoreCategory;
